import { useState, useRef, useEffect } from "react";
import { router } from "@inertiajs/react";
import useDelayedLoading from "@/hooks/use-delayed-loading";
import { mergedQueryParams } from "@/utils/queryParams";

const DEBOUNCE_DELAY = 300;

interface TableSearchProps {
  label?: string;
  placeholder?: string;
  queryParam: string;
  initialValue?: string;
  only?: string[];
  onLoadingChange?: (loading: boolean) => void;
}

const TableSearch: React.FC<TableSearchProps> = ({
  label,
  placeholder = "Search...",
  queryParam,
  initialValue = "",
  only,
  onLoadingChange,
}) => {
  const [query, setQuery] = useState(initialValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef(initialValue);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const { loading, start, stop } = useDelayedLoading();

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  useEffect(() => {
    if (query === lastQueryRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      lastQueryRef.current = query;
      router.get(
        window.location.pathname,
        mergedQueryParams(queryParam, query),
        {
          only,
          preserveState: true,
          preserveScroll: true,
          replace: true,
          onStart: start,
          onFinish: stop,
        },
      );
    }, DEBOUNCE_DELAY);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, queryParam, only, start, stop]);

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
          {label}
        </label>
      )}
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="placeholder-cream-600 selection:bg-peach-300 focus:border-transparent focus:ring-peach-300 dark:placeholder-cream-500 text-cream-950 dark:bg-cream-800 dark:text-cream-100 w-full rounded-xl border border-transparent font-medium shadow focus:ring-2"
        autoComplete="off"
      />
    </div>
  );
};

export default TableSearch;
